import { DropdownMenuItem, DropdownMenuSeparator } from "@/components/podkit/dropdown/DropDown";
import { DropdownActions } from "@/components/podkit/dropdown/DropDownActions";
import { useToast } from "@/components/podkit/toasts/use-toast";
import type { PlainWorkflow } from "@/queries/automation-queries";
import { formatError } from "@/utils/errors";
import { useCallback, useState, type FC } from "react";
import { useNavigate } from "react-router-dom";
import { DeleteWorkflowDialog } from "./DeleteAutomationDialog";
import { workflowDetailsEditUrl } from "@/utils/automations";
import { IconEllipsisHorizontal } from "@/assets/icons/geist/IconEllipsisHorizontal";
import { Button } from "@/components/flexkit/Button";

export const WorkflowActionsDropdown: FC<{
    workflow: PlainWorkflow;
}> = ({ workflow }) => {
    const { toast } = useToast();
    const navigate = useNavigate();
    const [showDeleteModal, setShowDeleteModal] = useState(false);

    const handleCopyId = useCallback(async () => {
        try {
            await navigator.clipboard.writeText(workflow.id);
            toast({
                title: "Workflow ID copied to clipboard",
                description: workflow.id,
            });
        } catch (error) {
            toast({
                title: "Failed to copy workflow ID",
                description: formatError(error),
            });
        }
    }, [toast, workflow.id]);

    const handleEdit = useCallback(() => {
        navigate(workflowDetailsEditUrl({ id: workflow.id }));
    }, [navigate, workflow.id]);

    const handleDelete = useCallback(() => setShowDeleteModal(true), []);

    return (
        <>
            <DropdownActions
                triggerTestId="workflow-actions-dropdown-trigger"
                button={<Button variant="outline" aria-label="More actions" LeadingIcon={IconEllipsisHorizontal} />}
            >
                <DropdownMenuItem
                    onClick={handleEdit}
                    data-testid="workflow-actions-dropdown-edit"
                    data-tracking-id="edit-workflow-workflow-actions-dropdown"
                >
                    Edit
                </DropdownMenuItem>
                <DropdownMenuItem
                    onClick={handleCopyId}
                    data-testid="workflow-actions-dropdown-copy-id"
                    data-tracking-id="copy-id-workflow-actions-dropdown"
                >
                    Copy ID
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                    variant="destructive"
                    onClick={handleDelete}
                    data-testid="workflow-actions-dropdown-delete"
                    data-tracking-id="delete-workflow-actions-dropdown"
                >
                    Delete
                </DropdownMenuItem>
            </DropdownActions>
            <DeleteWorkflowDialog
                open={showDeleteModal}
                workflow={workflow}
                onClose={() => setShowDeleteModal(false)}
            />
        </>
    );
};
